import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import RestaurantProgress from 'components/RestaurantProgress';
import { LOCATION_PATH } from 'constants/path';
import { HeadquarterContext } from 'context/HeadquarterContext';
import { useAuth } from 'hooks/useAuth';
import { useCubohActionBar } from 'hooks/useCubohActionBar';

import { useContext } from 'react';
import { NavLink, UIMatch, useLocation, useMatches } from 'react-router';
import { isInternalAdminPage } from 'helpers/routeMatches';
import { isRecord } from 'helpers/typeGuards';
import { STATUSES } from 'hooks/useLocation';
import { useGetLocationHomePath } from 'hooks/useGetLocationHomePath';
import { LOCATION_HOME, OVERVIEW } from 'scopes/nav';

import { ChevronRightIconXs } from '@chownow/pantry/icons';

export default function NavGlobalDesktop() {
  const matches = useMatches();
  const { pathname } = useLocation();
  const { isUserCubohOnly } = useAuth();
  const { singleSelectedCompany } = useCubohActionBar();
  const { headquarter, location, locationStatus } = useContext(HeadquarterContext);
  const getLocationHomePath = useGetLocationHomePath();
  const hqName = headquarter?.name ?? 'Headquarters';
  // For Cuboh users, use the company name from singleSelectedCompany if available
  const cubohHqName = isUserCubohOnly && singleSelectedCompany ? singleSelectedCompany.name : null;
  const displayHqName = cubohHqName || hqName;

  const selectedLocation = isUserCubohOnly ? singleSelectedCompany : location;

  const isHqLevelPage = !pathname.includes(LOCATION_PATH);
  const isGlobalInternalPage = isInternalAdminPage(matches);
  const currentRoute = matches[matches.length - 1];
  const currentRouteKey = isRecord(currentRoute?.handle) ? currentRoute.handle.key : undefined;
  const isLocationOverviewPage = !isHqLevelPage && currentRouteKey === OVERVIEW;
  const isLocationHomePage = !isHqLevelPage && currentRouteKey === LOCATION_HOME;
  const crumbs = matches
    // get rid of any matches that don't have handle and crumb
    .filter(
      (match): match is UIMatch & { handle: { name: string; [key: string]: unknown } } =>
        isRecord(match.handle) && typeof match.handle.name === 'string',
    )
    // hide breadcrumbs with hideForHub
    .filter(match => !(isUserCubohOnly && match.handle.hideForHub))
    // Filter out 'Home' breadcrumb when on HQ home page
    .filter(match => !(isHqLevelPage && pathname === '/' && match.handle.name === 'Home'))
    // Filter out 'Overview' breadcrumb when on location home page
    .filter(match => !(isLocationOverviewPage && match.handle.name === 'Overview'))
    // Filter out 'Home' breadcrumb when on location home page
    .filter(match => !(isLocationHomePage && match.handle.name === 'Home'));
  if (locationStatus === STATUSES.UNAUTHORIZED || !selectedLocation || isGlobalInternalPage) {
    return null;
  }

  // Determine if HQ name should be a link or disabled text
  const isHqHome = isHqLevelPage && pathname === '/';
  const renderHqName = () => {
    if (isHqHome) {
      return (
        <Text maw={175} truncate="end" c="var(--content-secondary)" data-testid="HqName">
          {displayHqName}
        </Text>
      );
    }
    return (
      <Anchor
        maw={175}
        truncate="end"
        component={NavLink}
        to="/"
        data-testid="HqName"
        underline="never"
        end
      >
        {displayHqName}
      </Anchor>
    );
  };

  // Render restaurant name - disabled when on overview/home page, clickable otherwise
  const renderRestaurantName = () => {
    if (isLocationOverviewPage || isLocationHomePage) {
      return (
        <Text maw={175} truncate="end" c="var(--content-secondary)" data-testid="LocationName">
          {selectedLocation.name}
        </Text>
      );
    }
    return (
      <Anchor
        maw={175}
        truncate="end"
        component={NavLink}
        underline="never"
        to={getLocationHomePath(selectedLocation.id)}
      >
        {selectedLocation.name}
      </Anchor>
    );
  };

  return (
    <>
      {!isHqLevelPage && <RestaurantProgress location={selectedLocation} link />}

      <Breadcrumbs separator={<ChevronRightIconXs />} separatorMargin="xs2">
        {isHqLevelPage ? renderHqName() : renderRestaurantName()}
        {crumbs.map((crumb, index) => {
          // Check if this is the current page by comparing pathname or if it's the last crumb
          const isCurrentPage = crumb.pathname === pathname || index === crumbs.length - 1;
          return isCurrentPage ? (
            <Text key={crumb.handle.name} maw={175} truncate="end" c="var(--content-secondary)">
              {crumb.handle.name}
            </Text>
          ) : (
            <Anchor
              maw={175}
              truncate="end"
              key={crumb.handle.name}
              component={NavLink}
              to={crumb.pathname}
              underline="never"
              end
            >
              {crumb.handle.name}
            </Anchor>
          );
        })}
      </Breadcrumbs>
    </>
  );
}
